import React, { useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Divider,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import AddCardIcon from '@mui/icons-material/AddCard';
import ReplayIcon from '@mui/icons-material/Replay';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IconButton, Tooltip } from '@mui/material';
import HelpDialog from '../components/common/HelpDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPendingAdjustments, decideAdjustment, createWalletAdjustment,
  refundRide, waiveCharge, getReconciliation, getUserCharges,
} from '../services/financeService';
import { getRides } from '../services/rideService';
import { useToast } from '../lib/useToast';
import { getApiErrorMessage } from '../lib/toast';
import { useConfirm } from '../components/feedback/ConfirmDialog';
import { useCan } from '../auth/Can';
import FormDialog from '../components/common/FormDialog';
import PhoneUserAutocomplete, { PickedUser } from '../components/common/PhoneUserAutocomplete';

const inr = (n: number | null | undefined) =>
  `₹${Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const StatCard: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
  </Paper>
);

const FinanceOps: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { can } = useCan();
  const canAdjust = can('wallet:adjust');
  const canRefund = can('payments:write');

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Adjustment: user picked by phone
  const [adjUser, setAdjUser] = useState<PickedUser | null>(null);
  const [adjForm, setAdjForm] = useState({ amount: '', direction: 'credit', reason: '' });

  // Refund: rider picked by phone -> pick one of their rides
  const [refundUser, setRefundUser] = useState<PickedUser | null>(null);
  const [refundRideId, setRefundRideId] = useState<number | ''>('');
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });

  // Waive: driver picked by phone -> pick one of their outstanding charges
  const [waiveUser, setWaiveUser] = useState<PickedUser | null>(null);
  const [waiveChargeId, setWaiveChargeId] = useState<number | ''>('');
  const [waiveReason, setWaiveReason] = useState('');

  const recon = useQuery({ queryKey: ['reconciliation'], queryFn: () => getReconciliation() });
  const pending = useQuery({ queryKey: ['pending-adjustments'], queryFn: () => getPendingAdjustments() });

  // Rides for the chosen rider (refund picker)
  const refundRides = useQuery({
    queryKey: ['refund-rides', refundUser?.firebaseUid],
    queryFn: async () => {
      const res = await getRides({ riderUid: refundUser!.firebaseUid, limit: 25 });
      return (res.data?.data ?? res.data ?? []) as any[];
    },
    enabled: !!refundUser,
  });

  // Outstanding charges for the chosen driver (waive picker)
  const driverCharges = useQuery({
    queryKey: ['driver-charges', waiveUser?.firebaseUid],
    queryFn: () => getUserCharges(waiveUser!.firebaseUid),
    enabled: !!waiveUser,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['pending-adjustments'] });
    queryClient.invalidateQueries({ queryKey: ['reconciliation'] });
  };

  const decideMut = useMutation({
    mutationFn: ({ id, decision, reason }: { id: number; decision: 'approve' | 'reject'; reason?: string }) =>
      decideAdjustment(id, decision, reason),
    onSuccess: (_d, v) => { toast.success(`Adjustment ${v.decision}d`); invalidate(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const adjustMut = useMutation({
    mutationFn: () => createWalletAdjustment({
      userUid: adjUser!.firebaseUid,
      amount: Number(adjForm.amount),
      direction: adjForm.direction as 'credit' | 'debit',
      reason: adjForm.reason.trim(),
    }),
    onSuccess: () => {
      toast.success('Adjustment staged for approval');
      setAdjustOpen(false);
      setAdjUser(null);
      setAdjForm({ amount: '', direction: 'credit', reason: '' });
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const refundMut = useMutation({
    mutationFn: () => refundRide({
      rideId: Number(refundRideId),
      amount: refundForm.amount ? Number(refundForm.amount) : undefined,
      reason: refundForm.reason.trim(),
    }),
    onSuccess: (d: any) => {
      toast.success(`Refunded ${inr(d.refundAmount)} to rider wallet`);
      setRefundOpen(false);
      setRefundUser(null); setRefundRideId(''); setRefundForm({ amount: '', reason: '' });
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const waiveMut = useMutation({
    mutationFn: () => waiveCharge(Number(waiveChargeId), waiveReason.trim()),
    onSuccess: () => {
      toast.success('Charge waived');
      setWaiveOpen(false);
      setWaiveUser(null); setWaiveChargeId(''); setWaiveReason('');
      driverCharges.refetch();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const handleDecide = async (id: number, decision: 'approve' | 'reject') => {
    if (decision === 'reject') {
      const ok = await confirm({ title: 'Reject adjustment', message: 'Reject this wallet adjustment?', destructive: true, confirmText: 'Reject' });
      if (!ok) return;
      decideMut.mutate({ id, decision, reason: 'Rejected by admin' });
    } else {
      const ok = await confirm({ title: 'Approve adjustment', message: 'Approve and apply this wallet adjustment? This moves real balance.', confirmText: 'Approve' });
      if (!ok) return;
      decideMut.mutate({ id, decision });
    }
  };

  const totals = recon.data?.totals;
  const liability = recon.data?.walletLiability;

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap">
        <PaymentsIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Finance Operations</Typography>
        <Tooltip title="How finance operations work">
          <IconButton size="small" onClick={() => setHelpOpen(true)}><HelpOutlineIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Box flex={1} />
        {canAdjust && (
          <Button startIcon={<AddCardIcon />} variant="outlined" onClick={() => setAdjustOpen(true)}>
            New Adjustment
          </Button>
        )}
        {canRefund && (
          <>
            <Button startIcon={<ReplayIcon />} variant="outlined" color="warning" onClick={() => setRefundOpen(true)}>
              Refund Ride
            </Button>
            <Button startIcon={<MoneyOffIcon />} variant="outlined" color="error" onClick={() => setWaiveOpen(true)}>
              Waive Charge
            </Button>
          </>
        )}
      </Stack>

      {/* Reconciliation */}
      <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>Reconciliation (all-time)</Typography>
      {recon.isLoading ? (
        <CircularProgress size={26} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <StatCard label="Deposits" value={inr(totals?.deposits)} color="#10b981" />
          <StatCard label="Ride Payments" value={inr(totals?.ridePayments)} />
          <StatCard label="Driver Earnings" value={inr(totals?.rideEarnings)} />
          <StatCard label="Platform Charges" value={inr(totals?.platformChargesDebited)} />
          <StatCard label="Withdrawals" value={inr(totals?.withdrawals)} color="#ef4444" />
          <StatCard label="Bonuses Paid" value={inr(totals?.bonusesPaid)} color="#f59e0b" />
          <StatCard label="Wallet Liability (total)" value={inr(liability?.total)} color="#6366f1" />
          <StatCard label="Locked Balance" value={inr(liability?.locked)} />
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Pending adjustments */}
      <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
        Pending Wallet Adjustments {pending.data?.data?.length ? `(${pending.data.data.length})` : ''}
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pending.isLoading && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!pending.isLoading && (pending.data?.data?.length ?? 0) === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No pending adjustments.</TableCell></TableRow>
            )}
            {pending.data?.data?.map((tx: any) => (
              <TableRow key={tx.id} hover>
                <TableCell>{tx.id}</TableCell>
                <TableCell>
                  <Box sx={{ fontWeight: 600 }}>{tx.user?.name ?? tx.userId}</Box>
                  <Box sx={{ fontSize: 11, color: 'text.secondary' }}>{tx.user?.phone}</Box>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={tx.transactionType} color={tx.transactionType === 'credit' ? 'success' : 'error'} />
                </TableCell>
                <TableCell align="right">{inr(tx.amount)}</TableCell>
                <TableCell sx={{ maxWidth: 280, fontSize: 13 }}>{tx.description}</TableCell>
                <TableCell align="right">
                  {canAdjust ? (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="contained" color="success" disabled={decideMut.isPending} onClick={() => handleDecide(tx.id, 'approve')}>Approve</Button>
                      <Button size="small" variant="outlined" color="error" disabled={decideMut.isPending} onClick={() => handleDecide(tx.id, 'reject')}>Reject</Button>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">view only</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* New Adjustment dialog */}
      <FormDialog
        open={adjustOpen}
        title="New Wallet Adjustment"
        onClose={() => setAdjustOpen(false)}
        onSubmit={() => adjustMut.mutate()}
        submitting={adjustMut.isPending}
        submitText="Stage for Approval"
        disableSubmit={!adjUser || !adjForm.amount || !adjForm.reason}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Staged adjustments require approval by a second operator before the balance moves.
          </Typography>
          <PhoneUserAutocomplete value={adjUser} onChange={setAdjUser} label="User (search by phone or name)" />
          <TextField select label="Direction" value={adjForm.direction} onChange={(e) => setAdjForm({ ...adjForm, direction: e.target.value })} fullWidth>
            <MenuItem value="credit">Credit (add funds)</MenuItem>
            <MenuItem value="debit">Debit (remove funds)</MenuItem>
          </TextField>
          <TextField label="Amount (₹)" type="number" value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} fullWidth />
          <TextField label="Reason" value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} fullWidth multiline rows={2} />
        </Stack>
      </FormDialog>

      {/* Refund dialog */}
      <FormDialog
        open={refundOpen}
        title="Refund a Ride"
        onClose={() => setRefundOpen(false)}
        onSubmit={() => refundMut.mutate()}
        submitting={refundMut.isPending}
        submitText="Refund to Wallet"
        disableSubmit={!refundRideId || !refundForm.reason}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Find the rider by phone, pick the ride, and refund to their wallet. Leave amount blank for the full ride payment.
          </Typography>
          <PhoneUserAutocomplete
            value={refundUser}
            onChange={(u) => { setRefundUser(u); setRefundRideId(''); }}
            roleFilter="rider"
            label="Rider (search by phone or name)"
          />
          <TextField
            select label="Ride" value={refundRideId}
            onChange={(e) => setRefundRideId(Number(e.target.value))}
            fullWidth disabled={!refundUser || refundRides.isLoading}
            helperText={!refundUser ? 'Pick a rider first' : refundRides.isLoading ? 'Loading rides…' : 'Recent rides for this rider'}
          >
            {(refundRides.data ?? []).map((r: any) => (
              <MenuItem key={r.id} value={r.id}>
                #{r.id} · {r.pickupLocation?.slice(0, 18)} → {r.dropoffLocation?.slice(0, 18)} · {inr(r.totalFare ?? r.fare)} · {r.status}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Amount (₹, optional)" type="number" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} fullWidth />
          <TextField label="Reason" value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} fullWidth multiline rows={2} />
        </Stack>
      </FormDialog>

      {/* Waive dialog */}
      <FormDialog
        open={waiveOpen}
        title="Waive a Platform Charge"
        onClose={() => setWaiveOpen(false)}
        onSubmit={() => waiveMut.mutate()}
        submitting={waiveMut.isPending}
        submitText="Waive Charge"
        disableSubmit={!waiveChargeId || !waiveReason}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <PhoneUserAutocomplete
            value={waiveUser}
            onChange={(u) => { setWaiveUser(u); setWaiveChargeId(''); }}
            roleFilter="driver"
            label="Driver (search by phone or name)"
          />
          <TextField
            select label="Outstanding charge" value={waiveChargeId}
            onChange={(e) => setWaiveChargeId(Number(e.target.value))}
            fullWidth disabled={!waiveUser || driverCharges.isLoading}
            helperText={!waiveUser ? 'Pick a driver first' : driverCharges.isLoading ? 'Loading charges…' : ((driverCharges.data?.data?.length ?? 0) === 0 ? 'No outstanding charges' : 'Outstanding charges')}
          >
            {(driverCharges.data?.data ?? []).map((c: any) => (
              <MenuItem key={c.id} value={c.id}>
                #{c.id} · {c.chargeType} · {inr(c.amount)} · {c.status}{c.dueDate ? ` · due ${String(c.dueDate).slice(0, 10)}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Reason" value={waiveReason} onChange={(e) => setWaiveReason(e.target.value)} fullWidth multiline rows={2} />
        </Stack>
      </FormDialog>

      <HelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="How Finance Operations work"
        intro="Three admin money actions plus a read-only reconciliation summary. Every action is recorded in the Audit Log."
        flows={[
          {
            title: 'Wallet Adjustment (two-person approval)',
            steps: [
              { label: 'Find the user by phone', detail: 'No raw IDs — type a phone/name and pick the user.' },
              { label: 'Choose credit or debit + amount + reason' },
              { label: 'Staged as PENDING', detail: 'No money moves yet — it appears in "Pending Wallet Adjustments".' },
              { label: 'A second operator Approves or Rejects', detail: 'Approve → balance moves atomically. Reject → nothing changes.' },
            ],
          },
          {
            title: 'Refund a Ride (to wallet)',
            steps: [
              { label: 'Find the rider by phone, pick the ride' },
              { label: 'Rider wallet is credited + ride marked refunded', detail: 'This is a WALLET refund, not a card/UPI gateway reversal.' },
            ],
          },
          {
            title: 'Waive a Charge',
            steps: [
              { label: 'Find the driver by phone, pick an outstanding charge' },
              { label: 'Charge marked WAIVED', detail: 'The driver no longer owes that platform fee.' },
            ],
          },
        ]}
        notes={[
          'Reconciliation cards are read-only totals across all wallets (deposits, ride payments, earnings, charges, withdrawals, liability).',
          'Wallet adjustments need wallet:adjust; refunds and waivers need payments:write.',
          'Refunds credit the in-app wallet only — to reverse a real card/UPI charge, use the payment gateway separately.',
        ]}
      />
    </Box>
  );
};

export default FinanceOps;
